import React from 'react';
import useHttp from '../../../hooks/http.hook';
import './ExpenseStatsList.css';

const PAGE_SIZE = 20;

function getScrollParent(el) {
  let node = el?.parentElement;
  while (node && node !== document.body) {
    const style = window.getComputedStyle(node);
    const overflowY = style.overflowY || style.overflow;
    if (/(auto|scroll|overlay)/.test(overflowY)) return node;
    node = node.parentElement;
  }
  return null;
}

function ExpenseStatsList({ branchKey, periodId, active = false }) {
  const { requestWithMeta } = useHttp();
  const [draft, setDraft] = React.useState('');
  const [tags, setTags] = React.useState([]);
  const [items, setItems] = React.useState([]);
  const [totalPrice, setTotalPrice] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [fetchError, setFetchError] = React.useState(false);
  const [retryTick, setRetryTick] = React.useState(0);
  const sentinelRef = React.useRef(null);
  const generationRef = React.useRef(0);
  const loadingMoreRef = React.useRef(false);

  const branch = branchKey || 'store';
  const period = Number(periodId) || 1;

  const nfMoney = React.useMemo(
    () => new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }),
    []
  );

  const fetchPage = React.useCallback(async (pageNum) => {
    const params = new URLSearchParams();
    params.set('periodId', String(period));
    params.set('page', String(pageNum));
    params.set('limit', String(PAGE_SIZE));
    tags.forEach((tag) => params.append('tag', tag));
    return requestWithMeta(
      `/operator/stats/expenses/branch/${encodeURIComponent(branch)}?${params.toString()}`,
      'GET'
    );
  }, [branch, period, requestWithMeta, tags]);

  React.useEffect(() => {
    if (!active) return undefined;
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    let cancelled = false;

    setLoading(true);
    setLoadingMore(false);
    loadingMoreRef.current = false;
    setFetchError(false);
    setItems([]);
    setPage(1);
    setTotalPages(0);
    setTotalPrice(0);

    (async () => {
      try {
        const res = await fetchPage(1);
        if (cancelled || generation !== generationRef.current) return;
        if (!res?.ok) {
          setItems([]);
          setFetchError(true);
          return;
        }
        const data = res?.data ?? {};
        const list = Array.isArray(data.expenses) ? data.expenses : [];
        const pagination = data.pagination ?? {};
        const price = Number(data.totalPrice ?? 0);
        setItems(list.map((row, index) => ({
          productName: String(row?.productName ?? '').trim() || '—',
          price: Number.isFinite(Number(row?.price)) ? Number(row.price) : 0,
          key: `${index}`,
        })));
        setTotalPrice(Number.isFinite(price) ? price : 0);
        setPage(Number(pagination.page) || 1);
        setTotalPages(Number(pagination.totalPages) || 0);
        setFetchError(false);
      } catch (_) {
        if (!cancelled && generation === generationRef.current) {
          setItems([]);
          setFetchError(true);
        }
      } finally {
        if (!cancelled && generation === generationRef.current) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [active, fetchPage, retryTick]);

  const hasMore = page < totalPages;

  const loadMore = React.useCallback(async () => {
    if (loadingMoreRef.current || !hasMore) return;
    const generation = generationRef.current;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const next = page + 1;
      const res = await fetchPage(next);
      if (generation !== generationRef.current || !res?.ok) return;
      const data = res?.data ?? {};
      const more = Array.isArray(data.expenses) ? data.expenses : [];
      const pagination = data.pagination ?? {};
      setItems((prev) => [
        ...prev,
        ...more.map((row, index) => ({
          productName: String(row?.productName ?? '').trim() || '—',
          price: Number.isFinite(Number(row?.price)) ? Number(row.price) : 0,
          key: `${prev.length + index}`,
        })),
      ]);
      setPage(Number(pagination.page) || next);
      setTotalPages(Number(pagination.totalPages) || 0);
      const price = Number(data.totalPrice ?? 0);
      if (Number.isFinite(price)) setTotalPrice(price);
    } catch (_) {
      // keep the rows already shown
    } finally {
      loadingMoreRef.current = false;
      if (generation === generationRef.current) setLoadingMore(false);
    }
  }, [fetchPage, hasMore, page]);

  React.useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !active || !hasMore || loading || loadingMore) return undefined;

    const root = getScrollParent(sentinel);
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        loadMore();
      },
      { root, rootMargin: '160px', threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [active, hasMore, items.length, loadMore, loading, loadingMore]);

  const addTag = () => {
    const value = draft.trim();
    if (!value) return;
    const exists = tags.some((tag) => tag.toLowerCase() === value.toLowerCase());
    setDraft('');
    if (exists) return;
    setTags((prev) => [...prev, value]);
  };

  const removeTag = (value) => {
    setTags((prev) => prev.filter((tag) => tag !== value));
  };

  if (!active) return null;

  return (
    <section
      id="analytics-section-expenses"
      className="expense-stats"
      aria-label="Расходы"
    >
      <div className="expense-stats-card">
        <div className="expense-stats-cardHead">
          <span className="expense-stats-cardTitle">Фильтр</span>
          <span className="expense-stats-cardHint">по названию</span>
        </div>
        <div className="expense-stats-composer">
          <input
            type="text"
            className="expense-stats-input"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                addTag();
              }
            }}
            placeholder="например, такси"
            autoComplete="off"
            spellCheck={false}
            aria-label="Новый тег"
          />
          <button
            type="button"
            className="expense-stats-add"
            onClick={addTag}
            disabled={!draft.trim()}
            aria-label="Добавить"
          >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <p className="expense-stats-hint">
          Введите название и добавьте тег, чтобы отфильтровать расходы.
        </p>
        {tags.length > 0 ? (
          <div className="expense-stats-tags" aria-label="Выбранные теги">
            {tags.map((tag) => (
              <span key={tag.toLowerCase()} className="expense-stats-tag">
                {tag}
                <button
                  type="button"
                  className="expense-stats-tagRemove"
                  onClick={() => removeTag(tag)}
                  aria-label={`Удалить тег ${tag}`}
                >
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M7 7l10 10M17 7L7 17" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                  </svg>
                </button>
              </span>
            ))}
          </div>
        ) : null}
      </div>

      {loading ? (
        <div className="expense-stats-loading" aria-busy="true">
          <div className="expense-stats-skel expense-stats-skel--total" />
          <div className="expense-stats-card">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="expense-stats-skel expense-stats-skel--row" />
            ))}
          </div>
        </div>
      ) : fetchError ? (
        <div className="expense-stats-fallback">
          <p className="expense-stats-fallbackText">Не удалось загрузить данные.</p>
          <button
            type="button"
            className="expense-stats-retry"
            onClick={() => setRetryTick((tick) => tick + 1)}
          >
            Повторить
          </button>
        </div>
      ) : (
        <>
          <div className="expense-stats-total">
            <div className="expense-stats-totalLabel">Итого</div>
            <div className="expense-stats-totalValue">
              {nfMoney.format(totalPrice)}
              <small>сом</small>
            </div>
          </div>
          {items.length === 0 ? (
            <p className="expense-stats-empty">Нет расходов за выбранный период и теги.</p>
          ) : (
            <div className="expense-stats-card">
              <div className="expense-stats-cardHead">
                <span className="expense-stats-cardTitle">Расходы</span>
                <span className="expense-stats-cardHint">название и сумма</span>
              </div>
              <ul className="expense-stats-list">
                {items.map((row, index) => (
                  <li key={`${row.productName}:${row.price}:${row.key}:${index}`} className="expense-stats-row">
                    <span className="expense-stats-name" title={row.productName}>{row.productName}</span>
                    <span className="expense-stats-price">
                      {nfMoney.format(row.price)}
                      <small>сом</small>
                    </span>
                  </li>
                ))}
              </ul>
              {loadingMore ? <p className="expense-stats-more">Загрузка…</p> : null}
              <div ref={sentinelRef} className="expense-stats-sentinel" />
            </div>
          )}
        </>
      )}
    </section>
  );
}

export default ExpenseStatsList;
